/**
 * MKS-349 follow-up — queries and mutations for the bulk "mark as paid"
 * action.
 *
 * The preview is a QUERY keyed on the cutoff date (it writes nothing and the
 * same date gives the same answer); the apply and the undo are mutations and
 * both invalidate everything a payment status feeds — the invoice list and its
 * badges, any open invoice detail, and the dashboard receivables cards.
 */
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import {
  applyBulkPaid,
  fetchBulkPaidBatches,
  previewBulkPaid,
  undoBulkPaid,
} from '../services/api/invoiceBulkPaid';
import { invalidateMany } from './queryUtils';

const BATCHES_KEY = ['invoices', 'bulk-paid', 'batches'];

/** Every surface a payment status feeds. `invalidateQueries` matches by prefix. */
function invalidatePaymentSurfaces(queryClient: ReturnType<typeof useQueryClient>): void {
  invalidateMany(queryClient, [['invoices'], ['invoice'], ['dashboard']]);
}

export function useBulkPaidPreview(issuedBefore: string, enabled: boolean) {
  return useQuery({
    queryKey: ['invoices', 'bulk-paid', 'preview', issuedBefore],
    queryFn: () => previewBulkPaid(issuedBefore),
    enabled: enabled && issuedBefore !== '',
    // The answer changes the moment anything is marked paid, so it is never
    // served from cache after a mutation has invalidated the prefix.
    staleTime: 0,
  });
}

export function useBulkPaidBatches(enabled: boolean) {
  return useQuery({
    queryKey: BATCHES_KEY,
    queryFn: () => fetchBulkPaidBatches(),
    enabled,
  });
}

export function useApplyBulkPaid() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (issuedBefore: string) => applyBulkPaid(issuedBefore),
    onSuccess: () => invalidatePaymentSurfaces(queryClient),
  });
}

export function useUndoBulkPaid() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (batchId: string) => undoBulkPaid(batchId),
    onSuccess: () => invalidatePaymentSurfaces(queryClient),
  });
}
